import { Trophy, HeartHandshake } from "lucide-react";

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const RANK_MEDAL_COLORS = {
  1: "bg-yellow-400 text-yellow-900",
  2: "bg-slate-300 text-slate-700",
  3: "bg-amber-600 text-amber-50",
};

const StudentList = ({ students, accentColor }) => (
  <ul className="divide-y divide-slate-100">
    {/* rank, not rollNo, is the unique key here: roll numbers restart from 1 per campus section,
        so the same roll number legitimately appears more than once in a list. */}
    {students.map((student) => (
      <li key={student.rank} className="flex items-center gap-4 px-6 py-3">
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
            RANK_MEDAL_COLORS[student.rank] || "bg-slate-100 text-slate-600"
          }`}
        >
          {student.rank}
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-800">{student.name}</p>
          <p className="text-xs text-slate-500">
            {student.section}
            {student.rollNo ? ` · Roll No. ${student.rollNo}` : ""}
          </p>
        </div>

        <span className="shrink-0 text-sm font-bold" style={{ color: accentColor }}>
          {formatPercent(student.overall)}
        </span>
      </li>
    ))}
  </ul>
);

// Reads live off the sheet's "Toppers & Support Needed" tab (topPerformers /
// studentsNeedingSupport from parseToppersSheet in sheetsApi.js) — no fixed student count or
// names here, so the lists track whatever the sheet currently ranks. The heading and subtitle are
// the sheet's own row 1 ("GRADE 10 TOPPERS & STUDENTS NEEDING SUPPORT") and row 2 ("Ranked by
// each student's Overall % ...") rather than fixed text, so they stay correct if the grade, exam,
// or campuses named there ever change.
const ToppersSection = ({
  topPerformers = [],
  studentsNeedingSupport = [],
  title = "Toppers & Students Needing Support",
  subtitle = "",
}) => {
  if (!topPerformers.length && !studentsNeedingSupport.length) return null;

  return (
    <div>
      <h2 className="text-2xl font-bold text-indigo-950">{title}</h2>
      {subtitle ? (
        <p className="mb-4 mt-1 text-sm text-slate-500">{subtitle}</p>
      ) : (
        <div className="mb-4" />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="overflow-hidden rounded-3xl bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-slate-100 px-6 py-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500 text-white">
              <Trophy size={16} />
            </div>
            <h3 className="text-sm font-semibold text-slate-700">Top Performers</h3>
          </div>
          <StudentList students={topPerformers} accentColor="#059669" />
        </div>

        <div className="overflow-hidden rounded-3xl bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-slate-100 px-6 py-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-500 text-white">
              <HeartHandshake size={16} />
            </div>
            <h3 className="text-sm font-semibold text-slate-700">Students Needing Support</h3>
          </div>
          <StudentList students={studentsNeedingSupport} accentColor="#DC2626" />
        </div>
      </div>
    </div>
  );
};

export default ToppersSection;
